import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  User, Mail, Smartphone, MapPin, ArrowLeft,
  Shield, CheckCircle, History, Info, Scissors,
  Settings, Globe, Phone, TrendingUp, DollarSign,
  Briefcase, CheckCircle2, XCircle, AlertCircle, Archive
} from "lucide-react";
import {
  getEmployeeFullDetails,
  EmployeeDetailsResponse
} from "../../../services/employeesApi";
import GlobalLoader from "../../../components/ui/GlobalLoader";
import PageHeader from "../../../components/ui/PageHeader";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";

export default function EmployeeDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [details, setDetails] = useState<EmployeeDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await getEmployeeFullDetails(id);
        if (data) {
          setDetails(data);
        } else {
          setError("Employee not found.");
        }
      } catch (err) {
        console.error("Failed to load employee details", err);
        setError("Could not load employee details.");
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id]);

  const chartData = useMemo(() => {
    if (!details?.sales_data) return [];
    return details.sales_data.labels.map((label, index) => ({
      name: label,
      value: details.sales_data.data[index] || 0
    }));
  }, [details]);

  if (loading) {
    return (
      <div className="flex h-[80vh] w-full items-center justify-center bg-gray-50">
        <GlobalLoader description="Loading staff profile..." />
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-gray-50 text-center p-6">
        <AlertCircle className="w-12 h-12 text-[#885ABB]/50 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Staff Profile Not Found</h2>
        <p className="text-gray-500 mb-6">{error || "The requested employee profile could not be reached."}</p>
        <button
          onClick={() => navigate(-1)}
          className="px-6 py-2 bg-[#885ABB] text-white rounded-full hover:bg-[#724b9e] transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  const { employee, statistics } = details;

  const formatCurrency = (amount?: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <PageHeader
        title="Staff Profile"
        icon={User}
        className="border-b-2 border-gray-300 shadow-sm"
        actions={
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">

        {/* Hero Section */}
        <div className="bg-white rounded-2xl p-8 border border-gray-200 shadow-sm relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-50 rounded-full blur-3xl -mr-32 -mt-32 opacity-50"></div>

          <div className="relative flex flex-col md:flex-row items-center md:items-start gap-8">
            {/* Avatar */}
            <div className="relative">
              <div className="w-32 h-32 rounded-full border-4 border-white shadow-md bg-purple-100 flex items-center justify-center overflow-hidden">
                {employee.user_image_url && employee.user_image_url !== "https://salonify.co.uk/img/default-avatar-user.png" ? (
                  <img src={employee.user_image_url} alt={employee.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-4xl font-bold text-[#885ABB]">
                    {employee.name.charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="absolute bottom-1 right-1 group">
                <div className={`w-6 h-6 border-4 border-white rounded-full ${employee.status === 'active' ? 'bg-green-500' : 'bg-gray-300'} transition-colors cursor-help`}></div>

                {/* Status Tooltip */}
                <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-gray-900 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-lg z-10">
                  {employee.status === 'active' ? 'Status: Active' : 'Status: Inactive'}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left space-y-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center justify-center md:justify-start gap-3">
                  {employee.name}
                  {employee.status === 'active' && (
                    <CheckCircle className="w-5 h-5 text-[#885ABB]" fill="currentColor" stroke="white" />
                  )}
                </h1>
                <p className="text-gray-500 mt-1">
                  {[employee.role, employee.group].filter(Boolean).join(" • ") || "Staff Member"}
                </p>
              </div>


            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full md:w-auto">
              <div className="p-4 rounded-xl bg-gray-50 border border-gray-100 text-center min-w-[100px]">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Earnings</p>
                <p className="text-xl font-bold text-gray-900 mt-1">{formatCurrency(statistics.total_earning)}</p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50 border border-purple-100 text-center min-w-[100px]">
                <p className="text-xs font-medium text-[#885ABB] uppercase tracking-wide">Completed</p>
                <p className="text-xl font-bold text-[#885ABB] mt-1">{statistics.completed_bookings}</p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50/70 border border-purple-100/70 text-center min-w-[100px]">
                <p className="text-xs font-medium text-[#885ABB]/80 uppercase tracking-wide">Approved</p>
                <p className="text-xl font-bold text-[#885ABB] mt-1">{statistics.approved_bookings}</p>
              </div>
              <div className="p-4 rounded-xl bg-purple-50/40 border border-purple-100/40 text-center min-w-[100px]">
                <p className="text-xs font-medium text-[#885ABB]/60 uppercase tracking-wide">Pending</p>
                <p className="text-xl font-bold text-[#885ABB] mt-1">{statistics.pending_bookings}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left Column */}
          <div className="space-y-6">
            {/* Trend Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden p-6">
              <h3 className="font-semibold text-[#885ABB] mb-6 flex items-center gap-2">
                <TrendingUp size={18} /> Sales Performance
              </h3>
              <div className="h-[200px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#885ABB" stopOpacity={0.1} />
                        <stop offset="95%" stopColor="#885ABB" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
                    <YAxis hide />
                    <RechartsTooltip />
                    <Area type="monotone" dataKey="value" stroke="#885ABB" fillOpacity={1} fill="url(#colorSales)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b-2 border-purple-200 bg-white flex items-center justify-between">
                <h3 className="font-semibold text-[#885ABB]">Contact Info</h3>
              </div>
              <div className="p-6 space-y-4">
                <DetailRow icon={Mail} label="Email Address" value={employee.email} />
                <DetailRow icon={Smartphone} label="Mobile Presence" value={employee.formatted_mobile || employee.mobile} />
                {employee.address && <DetailRow icon={MapPin} label="Operating Base" value={employee.address} />}
                {employee.country && <DetailRow icon={Globe} label="Region" value={employee.country} />}
              </div>
            </div>

            {/* Locations Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b-2 border-purple-200 bg-white">
                <h3 className="font-semibold text-[#885ABB]">Assigned Locations</h3>
              </div>
              <div className="p-6 space-y-3">
                {employee.locations && employee.locations.length > 0 ? (
                  employee.locations.map((loc: any, i: number) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <span className="text-sm font-medium text-gray-700">{typeof loc === 'string' ? loc : loc.name}</span>
                      <div className="w-2 h-2 rounded-full bg-green-400"></div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 italic">No branch assignments found.</p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column: Expertise */}
          <div className="lg:col-span-2 space-y-6">
            <div className="border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 pb-3 border-b-2 border-[#885ABB] inline-block">Professional Expertise</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {employee.services && employee.services.length > 0 ? (
                employee.services.map((service: string, idx: number) => (
                  <div key={idx} className="flex items-center gap-4 bg-white p-4 rounded-xl border border-gray-200 hover:border-[#885ABB]/30 transition-all cursor-pointer">
                    <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center text-[#885ABB]">
                      <Scissors size={18} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900">{service}</h4>
                      <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Expert Specialist</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="col-span-2 text-center py-12 bg-white rounded-xl border border-dashed border-gray-200">
                  <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900">No Services Mapped</h3>
                  <p className="text-gray-500">No professional expertise has been assigned to this profile.</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value }: any) {
  return (
    <div className="flex items-start gap-4">
      <div className="p-2 bg-gray-50 rounded-lg text-[#885ABB] shrink-0">
        <Icon size={18} />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-widest">{label}</p>
        <p className="text-sm text-gray-900 font-bold truncate max-w-[200px]">{value || "Not set"}</p>
      </div>
    </div>
  );
}
